import React, { useState, useEffect } from 'react';
import { ui } from '../../../config/uiSizes';

interface ExcludeResourceParametersProps {
  parameters?: Record<string, unknown>;
  onParametersChange?: (parameters: Record<string, unknown>) => void;
}

export const ExcludeResourceParameters: React.FC<ExcludeResourceParametersProps> = ({
  parameters,
  onParametersChange
}) => {
  const [exclusionType, setExclusionType] = useState<string>(
    (parameters?.['exclusionType'] as string) || 'pattern'
  );
  const [resourceIds, setResourceIds] = useState<string>(() => {
    const ids = parameters?.['resourceIds'];
    if (Array.isArray(ids)) {
      return ids.join(', ');
    }
    return '';
  });
  const [namePattern, setNamePattern] = useState<string>(
    (parameters?.['namePattern'] as string) || ''
  );
  const [propertyPath, setPropertyPath] = useState<string>(
    (parameters?.['propertyPath'] as string) || ''
  );
  const [propertyValue, setPropertyValue] = useState<string>(
    (parameters?.['propertyValue'] as string) || ''
  );
  const [caseSensitive, setCaseSensitive] = useState<boolean>(
    parameters?.['caseSensitive'] !== undefined ? (parameters['caseSensitive'] as boolean) : false
  );

  useEffect(() => {
    if (onParametersChange) {
      const params: Record<string, unknown> = {
        exclusionType,
        caseSensitive
      };

      if (exclusionType === 'id') {
        const idsArray = resourceIds
          .split(',')
          .map((id) => id.trim())
          .filter((id) => id.length > 0);
        if (idsArray.length > 0) {
          params['resourceIds'] = idsArray;
        }
      } else if (exclusionType === 'pattern') {
        if (namePattern) {
          params['namePattern'] = namePattern;
        }
      } else if (exclusionType === 'property') {
        if (propertyPath) {
          params['propertyPath'] = propertyPath;
        }
        if (propertyValue) {
          params['propertyValue'] = propertyValue;
        }
      }

      onParametersChange(params);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exclusionType, resourceIds, namePattern, propertyPath, propertyValue, caseSensitive]);

  return (
    <div className={`border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 ${ui.page.cardPadding}`}>
      <div className={`${ui.mb.title}`}>
        <h3 className={`${ui.text.sectionTitle} font-bold text-gray-900 dark:text-gray-100`}>Exclude Resource</h3>
        <p className={`${ui.text.label} text-gray-600 dark:text-gray-400 mt-1`}>
          Mark resources for exclusion from migration based on criteria.
        </p>
      </div>

      <div className="space-y-4">
        {/* Exclusion Type */}
        <div>
          <label className={`block ${ui.text.label} font-medium text-gray-700 dark:text-gray-300 mb-2`}>
            Exclusion Type
          </label>
          <select
            value={exclusionType}
            onChange={(e) => setExclusionType(e.target.value)}
            className={`w-full ${ui.input.base} border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <option value="id">By Resource ID</option>
            <option value="pattern">By Name Pattern</option>
            <option value="property">By Property Value</option>
          </select>
        </div>

        {/* Conditional Fields based on Exclusion Type */}
        {exclusionType === 'id' && (
          <div>
            <label className={`block ${ui.text.label} font-medium text-gray-700 dark:text-gray-300 mb-2`}>
              Resource IDs <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={resourceIds}
              onChange={(e) => setResourceIds(e.target.value)}
              placeholder="id-1, id-2, id-3"
              className={`w-full ${ui.input.base} border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
            <p className={`${ui.text.xs} text-gray-500 dark:text-gray-400 mt-1`}>
              Comma-separated list of resource IDs to exclude
            </p>
          </div>
        )}

        {exclusionType === 'pattern' && (
          <div>
            <label className={`block ${ui.text.label} font-medium text-gray-700 dark:text-gray-300 mb-2`}>
              Name Pattern (Regex) <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={namePattern}
              onChange={(e) => setNamePattern(e.target.value)}
              placeholder="^TEST_.*"
              className={`w-full ${ui.input.base} border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
            <p className={`${ui.text.xs} text-gray-500 dark:text-gray-400 mt-1`}>
              Regular expression pattern to match resource names
            </p>
          </div>
        )}

        {exclusionType === 'property' && (
          <>
            <div>
              <label className={`block ${ui.text.label} font-medium text-gray-700 dark:text-gray-300 mb-2`}>
                Property Path (JSONPath) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={propertyPath}
                onChange={(e) => setPropertyPath(e.target.value)}
                placeholder="$.division.id"
                className={`w-full ${ui.input.base} border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
              />
            </div>
            <div>
              <label className={`block ${ui.text.label} font-medium text-gray-700 dark:text-gray-300 mb-2`}>
                Property Value
              </label>
              <input
                type="text"
                value={propertyValue}
                onChange={(e) => setPropertyValue(e.target.value)}
                placeholder="value-to-match"
                className={`w-full ${ui.input.base} border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
              />
            </div>
          </>
        )}

        {/* Case Sensitive */}
        <div className={`flex items-center ${ui.gap.sm}`}>
          <input
            type="checkbox"
            id="caseSensitive"
            checked={caseSensitive}
            onChange={(e) => setCaseSensitive(e.target.checked)}
            className={`${ui.svg.sm} text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-400`}
          />
          <label htmlFor="caseSensitive" className={`${ui.text.label} text-gray-700 dark:text-gray-300`}>
            Case-sensitive pattern matching
          </label>
        </div>
      </div>
    </div>
  );
};

export default ExcludeResourceParameters;
